import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Typography,
  Tooltip,
  Box,
  Paper,
  Link,
  Skeleton,
  CircularProgress,
  IconButton,
  Button,
  Modal,
  Alert,
  Chip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  COLOR_GREEN,
  COLOR_PRIMARY,
  COLOR_RED,
  exchangeRates,
  MARKET_FINDER_TOOLTIP_CONTENT,
  monthNames,
  USER_WISE_SAVE_LIST_LIMIT,
} from 'src/utils/const';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import ConfigurableTooltip from 'src/App/Dashboards/Market/Components/Tooltips/ConfigurableTooltip';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import {
  checkifDemoMarketWithMarketId,
  convertCurrency,
  filterExampleSavedMarkets,
  formatCurrency,
  getGradeColor,
  getGradeAGradient,
  isMarketSearchAllowed,
  makeDownloadableBlob,
  makeLowerCase,
} from 'src/utils/functions';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import {
  fetchDefaultListDetails,
  setSelectedListLoadingAction,
  updateMarketInList,
} from 'src/store/Dashboard/actions';
import ColumnPicker from './ColumnPicker';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import {
  COLOR_GREY_400,
  COLOR_PRIMARY_1_300,
  COLOR_ERROR,
  COLOR_SUCCESS,
  COLOR_WARNING,
  COLOR_GREY_600,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_TEXT_2,
} from 'src/utils/colors';
import BedroomFilter from '../BedroomFilter/BedroomFilter';
import InvestorTypeFilter from '../InvestorTypeFilter/InvestorTypeFilter';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { useCanAccessExpertPlan, useCanAccessScalePlan } from 'src/hooks/useFeatureAccess';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import { User } from 'src/interfaces/iuser';
import ConfirmationModal from 'src/App/Dashboards/Market/Modals/ConfirmationModal';
import SavedMarketListsDropdown from './SavedMarketListsDropdown';
import { UpgradeAlertExpertToScale, UpgradeAlertStarterToExpert } from './Alerts';
import { useGetCreditsRemaining } from 'src/hooks/useCustomHooks';
import MarketExportModal from 'src/App/Dashboards/Market/Pages/ExplorerPage/MarketExportModal';
import { updateUserExplorerPageRowDataExportUsage } from 'src/App/Dashboards/Market/Pages/ExplorerPage/helper';
import { InfoOutlined } from '@mui/icons-material';

const graph_tooltip_style = {
  width: '10px',
  height: '10px',
  borderRadius: '900px',
  display: 'inline-block',
  marginRight: '4px',
};

// Helper function to format percentage
const formatPercentage = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '_';
  return `${value.toFixed(2)}%`;
};

// Helper function to extract text from ReactNode
const extractTextFromNode = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextFromNode).join('');
  }
  if (React.isValidElement(node)) {
    const { props } = node;
    if (props && props.children) {
      return extractTextFromNode(props.children);
    }
    // Add more specific handlers here if needed, e.g., for props.alt, props.value on inputs
  }
  return ''; // Default for unhandled complex elements
};

interface MiniSeasonalityGraphProps {
  seasonalityData: { [key: string]: number } | null;
  width?: number;
  height?: number;
  color?: string;
}

const MiniSeasonalityGraph: React.FC<MiniSeasonalityGraphProps> = ({
  seasonalityData,
  width = 80,
  height = 50,
  color = COLOR_GREY_400,
}) => {
  if (!seasonalityData || Object.keys(seasonalityData).length === 0) {
    return <Typography variant="caption">_</Typography>;
  }

  const monthData = monthNames.map((month: string, index: number) => ({
    name: month.substring(0, 3),
    value: seasonalityData[month] || 0,
    originalIndex: index, // Store original index for stable ranking
  }));

  // Sort a copy of monthData by value in descending order to determine ranks
  const sortedMonthData = [...monthData].sort((a, b) => b.value - a.value);

  // Create a map of originalIndex to its rank (0-11)
  const rankMap = new Map<number, number>();
  sortedMonthData.forEach((dataPoint, rank) => {
    rankMap.set(dataPoint.originalIndex, rank);
  });

  const CustomizedDot = (props: any) => {
    const { cx, cy, payload } = props; // payload is an item from monthData

    const rank = rankMap.get(payload.originalIndex);

    let dotColor;
    if (rank !== undefined) {
      if (rank < 4) {
        // Top 4 (ranks 0, 1, 2, 3)
        dotColor = COLOR_SUCCESS;
      } else if (rank < 8) {
        // Middle 4 (ranks 4, 5, 6, 7)
        dotColor = COLOR_WARNING;
      } else {
        // Bottom 4 (ranks 8, 9, 10, 11)
        dotColor = COLOR_ERROR;
      }
    } else {
      dotColor = COLOR_GREY_400; // Fallback, though should not be hit
    }

    return <circle cx={cx} cy={cy} r={2} stroke={dotColor} fill={dotColor} />;
  };

  return (
    <ResponsiveContainer width={width} height={height}>
      <LineChart data={monthData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <Line
          type="linear"
          dataKey="value"
          stroke={color}
          strokeWidth={1.5}
          dot={<CustomizedDot />}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Define all possible columns for the DataGrid
const getAllDataGridColumns = (currency: string, isFreeUser: boolean): (GridColDef & { tooltipKey?: string })[] => [
  {
    field: 'save',
    headerName: '',
    width: 50,
    sortable: false,
    filterable: false,
  },
  {
    field: 'id',
    headerName: '#',
    width: 50,
    sortable: false,
    hide: true,
  },
  {
    field: 'market_grade',
    renderHeader: () => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ConfigurableTooltip config={MARKET_FINDER_TOOLTIP_CONTENT.market_grade}>
          <Box sx={{ fontWeight: 500 }}>Market Grade</Box>
        </ConfigurableTooltip>
      </Box>
    ),
    width: 120,
    renderCell: (params: GridRenderCellParams) => {
      const isGradeA = (params.row.market_grade || '').toUpperCase().startsWith('A');
      return (
        <Box 
          sx={{ 
            color: isGradeA ? undefined : getGradeColor(params.row.market_grade), 
            fontWeight: 'bold',
            ...(isGradeA && getGradeAGradient()),
          }}
        >
          {params.row.market_grade}
        </Box>
      );
    },
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 260,
    renderCell: (params: GridRenderCellParams) => (
      <Link
        href={params.row.market_url}
        underline="none"
        target="_blank"
        sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}
      >
        <img
          src={`https://flagcdn.com/w80/${params.row.country_code?.toLowerCase()}.png`}
          alt="Country flag"
          style={{
            width: '30px',
            height: '20px',
            objectFit: 'cover',
            borderRadius: '4px',
            marginRight: '8px',
          }}
        />
        {params.row.name}
        <OpenInNewRoundedIcon sx={{ ml: '2px', fontSize: '12px' }} />

        {params.row.is_example && !checkifDemoMarketWithMarketId(params.row.market_id) && (
          <Chip
            label="Example"
            size="small"
            color="info"
            variant="outlined"
            sx={{
              transform: 'scale(0.8)',
              marginLeft: '4px',
            }}
          />
        )}

        {checkifDemoMarketWithMarketId(params.row.market_id) && (
          <Chip
            label="Demo"
            size="small"
            color="info"
            variant="outlined"
            sx={{
              transform: 'scale(0.8)',
              marginLeft: '4px',
            }}
          />
        )}
      </Link>
    ),
  },
  {
    field: 'regulation',
    headerName: 'Regulations',
    width: 150,
    renderCell: (params: GridRenderCellParams) => {
      const status = params.row.regulation;
      const licensePercent = params.row.license_percentage;

      return (
        <TooltipNew
          title={''}
          content={
            isFreeUser ? (
              <ul>
                <li>Regulation status is based on the % of listings with license info.</li>
                <br />
                <li>
                  <span style={{ fontWeight: 'bold' }}>Subscribe</span> to see the exact % of
                  licensed Airbnb listings in this market.
                </li>
              </ul>
            ) : licensePercent !== null ? (
              <ul>
                <li>Regulation status is based on the % of listings with license info.</li>
                <br />
                <li>
                  <span style={{ fontWeight: 'bold' }}>
                    {licensePercent > 0 ? licensePercent : 'less than 1'}%
                  </span>{' '}
                  of listings have a short-term rental license.
                </li>
              </ul>
            ) : (
              'License data N/A'
            )
          }
          arrow
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              background:
                status === 'strict'
                  ? COLOR_RED + '22'
                  : status === 'lenient'
                  ? COLOR_GREEN + '22'
                  : '#efefef',
              borderRadius: '900px',
              padding: '4px 8px',
              color: status === 'strict' ? COLOR_RED : status === 'lenient' ? COLOR_GREEN : '#555',
              textTransform: 'capitalize',
            }}
          >
            {status || '_'}
          </Box>
        </TooltipNew>
      );
    },
  },
  {
    field: 'occupancy_rate',
    headerName: 'Occupancy',
    width: 120,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.occupancy_rate as number),
  },
  {
    field: 'supply',
    headerName: 'No. of listings',
    width: 120,
    renderCell: (params: GridRenderCellParams) =>
      params.row.supply ? formatCurrency(String(params.row.supply as number)) : '_',
  },
  {
    field: 'adr',
    headerName: 'ADR',
    width: 100,
    renderCell: (params: GridRenderCellParams) =>
      params.row.adr
        ? exchangeRates[currency].symbol +
          formatCurrency(String(convertCurrency(currency, params.row.adr as number)))
        : '_',
  },
  {
    field: 'revenue',
    headerName: 'Revenue',
    width: 120,
    renderCell: (params: GridRenderCellParams) =>
      params.row.revenue
        ? exchangeRates[currency].symbol +
          formatCurrency(String(convertCurrency(currency, params.row.revenue as number)))
        : '_',
  },
  {
    field: 'property_price_2bd',
    headerName: 'Property Price (2bd)',
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.row.property_price_2bd
        ? exchangeRates[currency].symbol +
          formatCurrency(String(convertCurrency(currency, params.row.property_price_2bd as number)))
        : '_',
    tooltipKey: 'property_price_2bd',
  },
  {
    field: 'short_term_rental_yield_2bd',
    headerName: 'STR Yield (2bd)',
    width: 120,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.short_term_rental_yield_2bd as number),
    tooltipKey: 'short_term_rental_yield',
  },
  {
    field: 'rent_2bd',
    headerName: 'Rent Price (2bd)',
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.row.rent_2bd
        ? exchangeRates[currency].symbol +
          formatCurrency(String(convertCurrency(currency, params.row.rent_2bd as number)))
        : '_',
    tooltipKey: 'rent_price_2bd',
  },
  {
    field: 'rent_gap_2bd',
    headerName: 'Rent Gap (2bd)',
    width: 120,
    renderCell: (params: GridRenderCellParams) =>
      params.row.rent_gap_2bd
        ? exchangeRates[currency].symbol +
          formatCurrency(String(convertCurrency(currency, params.row.rent_gap_2bd as number)))
        : '_',
    tooltipKey: 'rent_gap',
  },
  {
    field: 'yoy_supply_change',
    headerName: 'YoY Supply',
    width: 120,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.yoy_supply_change as number),
    tooltipKey: 'yoy_supply_change',
  },
  {
    field: 'yoy_revenue_change',
    headerName: 'YoY RevPAR',
    width: 120,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.yoy_revenue_change as number),
    tooltipKey: 'yoy_revenue_change',
  },
  {
    field: 'seasonality_graph',
    headerName: 'Seasonality',
    width: 150,
    renderCell: (params: GridRenderCellParams) => {
      const seasonalityData = params.row.seasonality_graph
        ? JSON.parse(params.row.seasonality_graph as string)
        : null;
      return seasonalityData ? (
        <TooltipNew
          title={''}
          content={
            <>
              <ul>
                <li>
                  <Box sx={{ ...graph_tooltip_style, backgroundColor: COLOR_SUCCESS }}></Box> Peak
                  season
                </li>
                <li>
                  <Box sx={{ ...graph_tooltip_style, backgroundColor: COLOR_WARNING }}></Box>{' '}
                  Shoulder season
                </li>
                <li>
                  <Box sx={{ ...graph_tooltip_style, backgroundColor: COLOR_RED }}></Box> Low season
                </li>
              </ul>
            </>
          }
        >
          <Box>
            <MiniSeasonalityGraph seasonalityData={seasonalityData} />
          </Box>
        </TooltipNew>
      ) : (
        <Typography variant="caption">_</Typography>
      );
    },
    sortable: false,
    filterable: false,
  },
  { field: 'total_supply', headerName: 'Total Supply', width: 150 },
  {
    field: 'yoy_occupancy_change',
    headerName: 'YoY Occupancy',
    width: 130,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.yoy_occupancy_change as number),
  },
  {
    field: 'yoy_adr_change',
    headerName: 'YoY ADR',
    width: 130,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.yoy_adr_change as number),
  },
  {
    field: 'yoy_supply_change_2y',
    headerName: '2Y YoY Supply',
    width: 130,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.yoy_supply_change_2y as number),
  },
  {
    field: 'yoy_revenue_change_2y',
    headerName: '2Y YoY Revenue',
    width: 130,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.yoy_revenue_change_2y as number),
  },
  {
    field: 'yoy_occupancy_change_2y',
    headerName: '2Y YoY Occupancy',
    width: 140,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.yoy_occupancy_change_2y as number),
  },
  {
    field: 'yoy_adr_change_2y',
    headerName: '2Y YoY ADR',
    width: 130,
    renderCell: (params: GridRenderCellParams) =>
      formatPercentage(params.row.yoy_adr_change_2y as number),
  },
  {
    field: 'property_site_url',
    headerName: 'Properties for sale',
    width: 110,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams) => {
      const url = params.row.property_site_url;
      if (!url) return null;
      return (
        <Button
          size="small"
          variant="outlined"
          sx={{
            padding: '2px 10px',
            fontSize: '10px',
            textTransform: 'none',
            borderRadius: '900px',
            whiteSpace: 'nowrap',
          }}
          onClick={e => {
            e.stopPropagation();
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
        >
          View properties
        </Button>
      );
    },
  },
];

const DEFAULT_SELECTED_COLUMN_FIELDS = [
  'market_grade',
  'regulation',
  'occupancy_rate',
  'supply',
  'adr',
  'revenue',
  'property_price_2bd',
  'short_term_rental_yield_2bd',
  'rent_2bd',
  'rent_gap_2bd',
  'yoy_supply_change',
  'yoy_revenue_change',
  'seasonality_graph',
  'custom_note',
  'property_site_url',
];

interface ColumnPresetComponentProps {
  onDisplayedColumnsChange: (columns: GridColDef[]) => void;
  allPossibleColumns: GridColDef[];
  userPickedFields: string[];
  onUserPickedFieldsChange: (fields: string[]) => void;
  selectedBedroom: string;
  listingObject: any;
  selectedInvestorType: string;
  onInvestorTypeChange: (investorType: string) => void;
}

const ColumnPresetComponent: React.FC<ColumnPresetComponentProps> = ({
  onDisplayedColumnsChange,
  allPossibleColumns,
  userPickedFields,
  onUserPickedFieldsChange,
  selectedBedroom,
  listingObject,
  selectedInvestorType,
  onInvestorTypeChange,
}) => {
  const dispatch = useDispatch();

  const [pickerOpen, setPickerOpen] = useState(false);

  const handleApplyColumns = (newSelectedFields: string[]) => {
    onUserPickedFieldsChange(newSelectedFields);
    setPickerOpen(false);
  };

  useEffect(() => {
    const displayed = allPossibleColumns.filter(
      col => userPickedFields.includes(col.field) || ['save', 'id', 'name'].includes(col.field)
    );
    onDisplayedColumnsChange(displayed);
  }, [allPossibleColumns, userPickedFields, onDisplayedColumnsChange]);

  useEffect(() => {
    if (!listingObject.id) return;

    const lists = [
      {
        is_default: true,
        list_id: listingObject.id,
        list_name: listingObject.name,
      },
    ];
    dispatch(setSelectedListLoadingAction(true));
    dispatch(
      fetchDefaultListDetails(
        lists,
        0,
        selectedBedroom === 'All' ? null : selectedBedroom === 'Studio' ? '0' : selectedBedroom
      )
    );
  }, [selectedBedroom]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        variant="outlined"
        onClick={() => setPickerOpen(true)}
        startIcon={<ViewWeekOutlinedIcon />}
        sx={{
          background: COLOR_PRIMARY_1_300 + '22',
          fontWeight: 600,
          height: '40px',
          borderColor: 'rgba(0, 0, 0, 0.23)',
        }}
      >
        Customize Columns
      </Button>
      <ColumnPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        initialSelectedColumns={userPickedFields}
        onApplyColumns={handleApplyColumns}
        initialInvestorType={selectedInvestorType}
        onApplyInvestorType={onInvestorTypeChange}
      />
    </Box>
  );
};

const ExportButton = ({
  columns,
  rows,
  userPickedFields,
  isFreeUser,
  handleOnSampleClick,
}: {
  columns: GridColDef[];
  rows: any[];
  userPickedFields: string[];
  isFreeUser: boolean;
  handleOnSampleClick: (planName: string) => void;
}) => {
  const isScaleUser = useCanAccessScalePlan();
  const dispatch = useDispatch();

  const { creditsRemaining, creditsUsed } = useGetCreditsRemaining();
  const [showExportCreditWarningModal, setShowExportCreditWarningModal] = useState<boolean>(false);

  const handleExport = (isPremium: boolean = false) => {
    // Always export all visible columns (passed as columns prop)
    // Exclude only 'save' column (which is for UI only)

    const finalColsToExport = columns.filter(col => col.field !== 'save');

    const header = finalColsToExport.map(col => `"${col.headerName}"`).join(',');

    const freeExportFields = ['id', 'name', 'regulation', 'occupancy_rate', 'supply', 'adr'];

    const csvRows = (!isPremium ? rows : rows.slice(0, creditsRemaining)).map(row =>
      finalColsToExport
        .map(col => {
          let cellValueString: string;
          const field = col.field;

          if (!isPremium && !freeExportFields.includes(field)) {
            cellValueString = isScaleUser ? 'Unlock to View' : 'Scale Plan Required';
          } else {
            // Special handling for seasonality_graph
            if (field === 'seasonality_graph') {
              let seasonalityData = null;
              try {
                seasonalityData = row.seasonality_graph ? JSON.parse(row.seasonality_graph) : null;
              } catch (e) {
                seasonalityData = null;
              }
              if (isFreeUser) {
                cellValueString = isScaleUser ? 'Unlock to View' : 'Scale Plan Required';
              } else if (seasonalityData) {
                // Convert seasonalityData to array of values
                const values = Object.values(seasonalityData)
                  .map(Number)
                  .filter(v => !isNaN(v));
                if (values.length > 0) {
                  const rMax = Math.max(...values);
                  const rMin = Math.min(...values);
                  const rAvg = values.reduce((a, b) => a + b, 0) / values.length;
                  const percent = rAvg !== 0 ? Math.round(((rMax - rMin) / rAvg) * 100) : 0;
                  let label = '';
                  if (percent > 75) label = `High (${percent}%)`;
                  else if (percent > 50) label = `Mid (${percent}%)`;
                  else label = `Low (${percent}%)`;
                  cellValueString = label;
                } else {
                  cellValueString = '';
                }
              } else {
                cellValueString = '';
              }
            } else if (col.renderCell) {
              const params: GridRenderCellParams = {
                id: row.id,
                field: field,
                value: row[field],
                row: row,
                colDef: col as any,
                api: null as any,
                hasFocus: false,
                tabIndex: -1,
                formattedValue: row[field],
                rowNode: { id: row.id, data: row } as any,
                cellMode: 'view',
                getValue: (id, f) => row[f],
              };
              const renderedNode = col.renderCell(params);
              cellValueString = extractTextFromNode(renderedNode);
            } else {
              const rawValue = row[field];
              cellValueString = rawValue !== undefined && rawValue !== null ? String(rawValue) : '';
            }
            // If the cell value is 'Locked', output 'Paid plan required' in the CSV
            if (cellValueString.trim() === 'Locked') {
              cellValueString = isScaleUser ? 'Unlock to View' : 'Paid plan required';
            }
          }
          return `"${cellValueString.replace(/"/g, '""')}"`;
        })
        .join(',')
    );

    const csvString = [header, ...csvRows].join('\n');
    makeDownloadableBlob(csvString, 'my_saved_markets', true, true);

    if (isPremium && isScaleUser) {
      updateUserExplorerPageRowDataExportUsage(Math.min(creditsRemaining, rows.length), dispatch);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => setShowExportCreditWarningModal(true)}
        startIcon={<FileDownloadOutlinedIcon />}
        sx={{
          background: COLOR_PRIMARY_1_300 + '22',
          fontWeight: 600,
          height: '40px',
          borderColor: 'rgba(0, 0, 0, 0.23)',
        }}
      >
        Export
      </Button>

      {showExportCreditWarningModal && (
        <MarketExportModal
          open={showExportCreditWarningModal}
          handleClose={() => setShowExportCreditWarningModal(false)}
          handleExportRows={handleExport}
          handleOnSampleClick={handleOnSampleClick}
          numberOfRows={rows.length}
          handleExportRowsFree={() => handleExport(false)}
          setCreditDeductModalOpen={setShowExportCreditWarningModal}
          currentRequestType={isFreeUser ? 'free' : 'premium'}
        />
      )}
    </>
  );
};

const SavedMarketListPage = ({
  isLoading,
  handleOnSampleClick,
  openSearchModal,
  setOpenSearchModal,
  selectedMarket,
  selectedList,
  currency,
  isLoggedIn,
  isMobileView,
  dummyMarketID,
}: {
  isLoading: boolean;
  handleOnSampleClick?: any;
  openSearchModal: boolean;
  setOpenSearchModal: (open: boolean) => void;
  selectedMarket: any;
  selectedList: any;
  currency: string;
  isLoggedIn: boolean;
  isMobileView: boolean;
  dummyMarketID: string;
}) => {
  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const marketDetailsObject = savedMarketsState.data?.marketDetails || {};
  const listingObject = savedMarketsState.data;
  const dispatch = useDispatch();
  const [selectedBedroom, setSelectedBedroom] = useState<string>('All');
  const [selectedInvestorType, setSelectedInvestorType] = useState<string>('buy');
  const [userPickedColumnFields, setUserPickedColumnFields] = useState<string[]>(
    DEFAULT_SELECTED_COLUMN_FIELDS
  );
  const [confirmDeleteMarket, setConfirmDeleteMarket] = useState<{
    marketName: string;
    handleDelete: () => void;
  } | null>(null);

  const user: User = useSelector((state: any) => state.dashboard.user);

  const isFreeUser = !useCanAccessExpertPlan();
  const upgradeMessage = useUpgradeMesageExpertFeature();

  const allPossibleColumns = useMemo(() => getAllDataGridColumns(currency, isFreeUser), [
    currency,
    isFreeUser,
  ]);

  const marketList = useMemo(() => {
    let markets = Object.values(savedMarketsState.data?.marketDetails || {})
      .filter(market => market !== null)
      .map((market: any) => ({
        ...market,
        id: market.market_id,
      }));

    return markets;
  }, [selectedBedroom, savedMarketsState]);

  const [currentColumns, setCurrentColumns] = useState<GridColDef[]>(() => {
    return allPossibleColumns.filter(
      col =>
        DEFAULT_SELECTED_COLUMN_FIELDS.includes(col.field) ||
        ['save', 'id', 'name'].includes(col.field)
    );
  });

  useEffect(() => {
    const userStrategy = user.userStrategy?.toLowerCase();
    if (userStrategy === 'buy') {
      setSelectedInvestorType('buy');
    } else if (userStrategy === 'rent') {
      setSelectedInvestorType('rent');
    } else if (userStrategy === 'manage') {
      setSelectedInvestorType('manage');
    } else {
      setSelectedInvestorType('buy');
    }
  }, [user]);

  useEffect(() => {
    let newSelectedFields = [...userPickedColumnFields];
    const investorType = selectedInvestorType;

    if (investorType === 'buy') {
      if (!newSelectedFields.includes('property_price_2bd'))
        newSelectedFields.push('property_price_2bd');
      if (!newSelectedFields.includes('short_term_rental_yield_2bd'))
        newSelectedFields.push('short_term_rental_yield_2bd');
      newSelectedFields = newSelectedFields.filter(
        field => field !== 'rent_2bd' && field !== 'rent_gap_2bd'
      );
    } else if (investorType === 'rent') {
      if (!newSelectedFields.includes('rent_2bd')) newSelectedFields.push('rent_2bd');
      if (!newSelectedFields.includes('rent_gap_2bd')) newSelectedFields.push('rent_gap_2bd');
      newSelectedFields = newSelectedFields.filter(
        field => field !== 'property_price_2bd' && field !== 'short_term_rental_yield_2bd'
      );
    } else if (investorType === 'manage') {
      newSelectedFields = newSelectedFields.filter(
        field =>
          field !== 'property_price_2bd' &&
          field !== 'short_term_rental_yield_2bd' &&
          field !== 'rent_2bd' &&
          field !== 'rent_gap_2bd'
      );
    }
    setUserPickedColumnFields(Array.from(new Set(newSelectedFields)));
  }, [selectedInvestorType]);

  const columnsWithDynamicCells = useMemo(() => {
    const freeUserAllowedBaseFields = [
      'id',
      'market_grade',
      'name',
      'regulation',
      'supply',
      'occupancy_rate',
      'adr',
      'property_site_url',
    ];
    const freeUserAllowedFields = ['save', ...freeUserAllowedBaseFields];

    let ctaButtonAdded = false; // Flag to ensure CTA is added only once

    return currentColumns.map(colDef => {
      const newColDef = { ...colDef };

      if (isFreeUser && !freeUserAllowedFields.includes(newColDef.field)) {
        (newColDef as any).isLockedForFreeUser = true; // Mark as locked for export logic and other potential uses

        newColDef.renderCell = () => (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontStyle: 'italic',
                color: '#000',
                filter: 'blur(4px)',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                padding: '2px 6px',
              }}
            >
              Locked
            </Typography>
          </Box>
        );
      } else {
        // Apply specific renderCells for 'save' and 'id' or keep original
        if (newColDef.field === 'save') {
          newColDef.renderCell = (params: GridRenderCellParams) => (
            <Box display="flex" justifyContent="center" width="100%">
              <IconButton
                size="small"
                onClick={() => {
                  if (listingObject && params.row.market_id && params.row.name) {
                    setConfirmDeleteMarket({
                      marketName: params.row.name,
                      handleDelete: () => {
                        dispatch(
                          updateMarketInList(
                            listingObject,
                            Number(params.row.market_id),
                            listingObject.marketIDs[params.row.market_id] === 'saved'
                              ? 'remove'
                              : 'add',
                            params.row.name
                          )
                        );
                      },
                    });
                  }
                }}
              >
                <>
                  {listingObject?.marketIDs[params.row.market_id] === 'saved' && (
                    <Tooltip title="Remove from Saved Markets">
                      <BookmarkIcon sx={{ fontSize: 20, color: COLOR_PRIMARY }} />
                    </Tooltip>
                  )}
                  {listingObject?.marketIDs[params.row.market_id] === 'saving' && (
                    <CircularProgress size={20} />
                  )}
                  {(!listingObject?.marketIDs[params.row.market_id] ||
                    (listingObject?.marketIDs[params.row.market_id] !== 'saved' &&
                      listingObject?.marketIDs[params.row.market_id] !== 'saving')) && (
                    <BookmarkIcon sx={{ fontSize: 20, color: 'grey' }} />
                  )}
                </>
              </IconButton>
            </Box>
          );
        } else if (newColDef.field === 'id') {
          newColDef.renderCell = (params: GridRenderCellParams) =>
            marketList.findIndex(m => m.id === params.id) + 1;
        } else if (newColDef.field === 'market_grade') {
          newColDef.renderHeader = () => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ConfigurableTooltip config={MARKET_FINDER_TOOLTIP_CONTENT.market_grade}>
                <Box sx={{ fontWeight: 500 }}>Market Grade</Box>
              </ConfigurableTooltip>
            </Box>
          );
        } else if (newColDef.field in MARKET_FINDER_TOOLTIP_CONTENT || (newColDef as any)?.tooltipKey in MARKET_FINDER_TOOLTIP_CONTENT) {
          newColDef.renderHeader = () => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ConfigurableTooltip config={MARKET_FINDER_TOOLTIP_CONTENT[(newColDef as any)?.tooltipKey || newColDef.field]}>
                <Box sx={{ fontWeight: 500 }}>{newColDef.headerName}</Box>
              </ConfigurableTooltip>
            </Box>
          );
        }
        (newColDef as any).isLockedForFreeUser = false;
      }
      return newColDef;
    });
  }, [currentColumns, dispatch, listingObject, marketList, isFreeUser, handleOnSampleClick]);

  return (
    <>
      <Grid
        container
        sx={
          isMobileView
            ? { paddingLeft: '10px', paddingRight: '0px' }
            : { padding: '20px', paddingTop: 0 }
        }
      >
        {/* {marketList.length > 0 && ( */}
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            ...(isMobileView && { mt: '30px', ml: '10px' }),
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item sx={{ ...(isMobileView && { ml: '10px' }) }}>
              {/* <Typography variant="h6">Saved Markets</Typography> */}
              <SavedMarketListsDropdown handleOnSampleClick={handleOnSampleClick} />
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mb: 2,
                gap: '10px',
                pt: '10px',
                minWidth: '440px',
              }}
            >
              <BedroomFilter
                selectedBedroom={selectedBedroom}
                onChange={setSelectedBedroom}
                isFreeUser={isFreeUser}
                handleOnSampleClick={handleOnSampleClick}
              />
              <ColumnPresetComponent
                onDisplayedColumnsChange={setCurrentColumns}
                allPossibleColumns={allPossibleColumns}
                userPickedFields={userPickedColumnFields}
                onUserPickedFieldsChange={setUserPickedColumnFields}
                selectedBedroom={selectedBedroom}
                listingObject={listingObject}
                selectedInvestorType={selectedInvestorType}
                onInvestorTypeChange={setSelectedInvestorType}
              />
              <ExportButton
                columns={columnsWithDynamicCells}
                rows={marketList}
                userPickedFields={userPickedColumnFields}
                isFreeUser={isFreeUser}
                handleOnSampleClick={handleOnSampleClick}
              />
            </Grid>
          </Grid>
        </Box>
        {/* )} */}
        {!isLoading &&
          user.subscriptionPlan === 'expert' &&
          filterExampleSavedMarkets(marketList, true).length >=
            USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan] && (
            <Grid xs={12}>
              <UpgradeAlertExpertToScale handleOnSampleClick={handleOnSampleClick} />
            </Grid>
          )}
        {!isLoading &&
          user.subscriptionPlan === 'starter' &&
          filterExampleSavedMarkets(marketList, true).length >=
            USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan] && (
            <Grid xs={12}>
              <UpgradeAlertStarterToExpert handleOnSampleClick={handleOnSampleClick} />
            </Grid>
          )}

        <Grid item xs={12}>
          <Paper style={{ width: '100%', position: 'relative' }}>
            {isLoading ? (
              <LoadingCard
                height={150}
                type="bars"
                noShadow={true}
                extraStyle={{ marginTop: '20px' }}
              />
            ) : (
              <>
                {isLoggedIn ? (
                  <DataGrid
                    rows={marketList}
                    columns={columnsWithDynamicCells}
                    rowHeight={50}
                    autoHeight
                  />
                ) : (
                  <Box>
                    <Alert severity="info">Sign Up and Search to save markets.</Alert>
                  </Box>
                )}

                {isFreeUser && marketList.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={() => handleOnSampleClick('expert')}
                    sx={{ position: 'absolute', top: '65px', right: '40px', color: '#fff' }}
                    startIcon={<LockOpenOutlinedIcon />}
                  >
                    {upgradeMessage}
                  </Button>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {openSearchModal && (
        <SearchResultModal
          open={openSearchModal}
          onClose={() => setOpenSearchModal(false)}
          selectedMarket={selectedMarket}
          selectedList={selectedList}
          isFreeUser={isFreeUser}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isLoggedIn={isLoggedIn}
          user={user}
          dummyMarketID={dummyMarketID}
        />
      )}

      {confirmDeleteMarket && (
        <ConfirmationModal
          open={confirmDeleteMarket !== null}
          onClose={() => setConfirmDeleteMarket(null)}
          title={
            <>
              Are you sure you want to remove{' '}
              <span style={{ color: COLOR_PRIMARY_2 }}>{confirmDeleteMarket.marketName}</span> from
              your Saved Markets list?
            </>
          }
          confirmButtonText="Yes"
          onConfirm={() => confirmDeleteMarket.handleDelete()}
          onCancel={() => setConfirmDeleteMarket(null)}
          cancelButtonText="No"
        />
      )}
    </>
  );
};

const SearchResultModal = ({
  open,
  onClose,
  selectedMarket,
  selectedList,
  isFreeUser,
  upgradeMessage,
  handleOnSampleClick,
  isLoggedIn,
  user,
  dummyMarketID,
}: {
  open: boolean;
  onClose: () => void;
  selectedMarket: any;
  selectedList: any;
  isFreeUser: boolean;
  upgradeMessage: string;
  handleOnSampleClick: (sample: string) => void;
  isLoggedIn: boolean;
  user: User;
  dummyMarketID: string;
}) => {
  const isScaleUser = useCanAccessScalePlan();

  const getSelectedMarket = (canUserSaveMoreMarkets: boolean) => {
    if (selectedMarket) {
      const marketName = selectedMarket.searchName;
      const countryCode = selectedMarket.countryCode;

      let countryParam: string = selectedMarket.country
        ? makeLowerCase(selectedMarket.country).replace(/ /g, '-')
        : '0';
      let cityParam: string = selectedMarket.city ? makeLowerCase(selectedMarket.city) : '0';
      let districtParam: string = makeLowerCase(selectedMarket.name);

      const redirectTo = `${window.location.origin}/airbnb-data/${countryParam}/${cityParam}/${districtParam}?market_id=${selectedMarket.id}`;

      const MarketSavedMessage = (
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <span>
            <img
              src={`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png`}
              alt={`${countryCode} flag`}
              style={{ marginRight: '8px', width: '30px', height: '20px', borderRadius: '4px' }}
            />
            {marketName}
            {`${
              canUserSaveMoreMarkets || selectedList.marketDetails[selectedMarket.id]
                ? ' is now on your Saved Markets!'
                : ' wasn’t added.'
            }`}
          </span>
        </Typography>
      );

      return {
        marketName: MarketSavedMessage,
        marketUrl: redirectTo,
      };
    }
    return null;
  };

  const canUserSaveMoreMarkets =
    isLoggedIn &&
    USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan] >
      filterExampleSavedMarkets(selectedList.marketDetails || {}).length;

  const marketInfo = getSelectedMarket(canUserSaveMoreMarkets);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLoggedIn) return;

    if (selectedList.marketDetails && selectedList.marketDetails[selectedMarket.id]) return;

    if (
      filterExampleSavedMarkets(selectedList.marketDetails || {}).length >=
      USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan]
    ) {
      return;
    }

    if (!canUserSaveMoreMarkets) return;

    if (selectedMarket && selectedList && selectedMarket.id) {
      dispatch(
        updateMarketInList(
          selectedList,
          Number(selectedMarket.id),
          'add',
          selectedMarket.searchName,
          true
        )
      );
    }
  }, []);

  // if (!canUserSaveMoreMarkets && isFreeUser) {
  //   handleOnSampleClick('expert');
  //   onClose();
  //   return <></>;
  // }

  useEffect(() => {
    const savedMarketsCount = filterExampleSavedMarkets(selectedList.marketDetails || {}).length;
    const limit = USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan];

    const hasSavedCurrentMarket = selectedMarket && selectedList.marketDetails[selectedMarket.id];

    const isUserMarketSearchAllowed = isMarketSearchAllowed(user, selectedMarket.id, dummyMarketID);

    if (
      isFreeUser &&
      savedMarketsCount >= limit &&
      !hasSavedCurrentMarket &&
      !isUserMarketSearchAllowed
    ) {
      handleOnSampleClick('expert');
      onClose();
    }
  }, [selectedList, selectedMarket, isFreeUser, user.subscriptionPlan]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          maxWidth: '90%',
          bgcolor: 'background.paper',
          border: 'none',
          outline: 'none',
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          borderRadius: '12px',
        }}
      >
        {marketInfo?.marketName}

        {!canUserSaveMoreMarkets &&
          !selectedList.marketDetails[selectedMarket.id] &&
          (!isScaleUser || !isMarketSearchAllowed(user, selectedMarket.id, dummyMarketID)) && (
            <Grid container justifyContent={'center'} sx={{ gap: '10px' }}>
              <Alert severity="warning">
                You’ve reached the{' '}
                {user.subscriptionPlan === 'expert'
                  ? 'Expert'
                  : user.subscriptionPlan === 'starter'
                  ? 'Starter'
                  : 'Scale'}{' '}
                plan limit for Saved Markets.{' '}
                <Link
                  href={undefined}
                  onClick={() => {
                    handleOnSampleClick(user.subscriptionPlan === 'expert' ? 'scale' : 'expert');
                    onClose();
                  }}
                  sx={{
                    color: COLOR_PRIMARY_2,
                    cursor: 'pointer',
                  }}
                >
                  Upgrade to{' '}
                  {user.subscriptionPlan === 'expert'
                    ? 'Scale'
                    : user.subscriptionPlan === 'starter'
                    ? 'Expert'
                    : ''}{' '}
                  Plan
                </Link>{' '}
                to create unlimited lists and save up to{' '}
                {user.subscriptionPlan === 'expert' ? 100 : 20} markets
                {user.subscriptionPlan === 'expert' ? ' per list' : ''}.
              </Alert>
            </Grid>
          )}

        {isScaleUser && !canUserSaveMoreMarkets && !selectedList.marketDetails[selectedMarket.id] && (
          <Grid container justifyContent={'center'} sx={{ gap: '10px' }}>
            <Alert severity="warning">
              You’ve reached the Scale plan limit for Saved Markets for the current list. Remove a
              market, choose a different list, or create a new one to save this market.
            </Alert>
          </Grid>
        )}

        <Grid container justifyContent={'center'} sx={{ gap: '10px' }}>
          <Button
            variant="contained"
            endIcon={<OpenInNewRoundedIcon />}
            sx={{ color: 'white' }}
            onClick={() => {
              logAmplitudeEvent('market_search_completed', {
                market_url: `${window.location.origin}${marketInfo?.marketUrl}`,
              });
              window.open(marketInfo?.marketUrl, '_blank');
            }}
          >
            Analyze {selectedMarket.searchName}
          </Button>
        </Grid>
      </Box>
    </Modal>
  );
};

export default SavedMarketListPage;
